import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Product } from '@/mocks/products';

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');

  const badgeMap: Record<string, string> = {
    'Mais Vendido': t('badge_best_seller', 'Mais Vendido'),
    'Novo': t('badge_new', 'Novo'),
    'Premium': t('badge_premium', 'Premium'),
    'Cask Strength': t('badge_cask_strength', 'Cask Strength'),
    'Clássico': t('badge_classic', 'Clássico'),
    'Exclusivo': t('badge_exclusive', 'Exclusivo'),
    'Edição Limitada': t('badge_limited', 'Edição Limitada'),
    'Navy Strength': t('badge_navy', 'Navy Strength'),
    'Importado': t('badge_imported', 'Importado'),
  };

  const descriptionEn: Record<string, string> = {
    'ardmore-islay-single-malt': 'Intense peat, sea smoke and wild honey notes. An Islay classic.',
    'glen-speyside-12': 'Ripe fruits, flower honey and gentle spices. The elegance of Speyside.',
    'highland-reserve-18': 'Rare complexity. Sherry, dark fruits and oriental spices in perfect harmony.',
    'blended-malt-reserve': 'The art of blending. Smoothness, depth and balance in every sip.',
    'peat-monster-cask': 'Extreme peat, cask strength. For uncompromising smoke lovers.',
    'lowland-gentle-spirit': 'Soft, floral and delicate. The perfect entry into the world of single malts.',
    'botanica-london-dry': 'Classic juniper, Sicilian lemon peel and coriander. The perfect London gin.',
    'floral-botanical-gin': 'Rose, lavender, hibiscus and violet petals. A floral gin of rare delicacy.',
    'small-batch-spiced-gin': 'Pink pepper, cardamom, ginger and juniper. Bold, spiced and memorable.',
    'mediterranean-dry-gin': 'Seville orange, thyme, rosemary and juniper. The Mediterranean in a bottle.',
    'navy-strength-gin': 'Power and character. Intense juniper, spices and naval strength in every sip.',
    'japanese-yuzu-gin': 'Yuzu, green tea, sakura and juniper. Japanese delicacy in gin form.',
  };

  const displayBadge = product.badge ? (badgeMap[product.badge] ?? product.badge) : null;
  const displayDescription = isEn ? (descriptionEn[product.slug] ?? product.description) : product.description;

  return (
    <div className="group relative bg-[#111111] border border-white/8 rounded-xl overflow-hidden hover:border-[#c9a84c]/40 transition-all duration-500 cursor-pointer">
      {/* Badge */}
      {displayBadge && (
        <div className="absolute top-4 left-4 z-10 bg-[#c9a84c] text-black text-[10px] tracking-widest uppercase font-bold px-3 py-1 rounded-full whitespace-nowrap">
          {displayBadge}
        </div>
      )}

      {/* Category tag */}
      <div className="absolute top-4 right-4 z-10 bg-black/60 backdrop-blur-sm text-white/60 text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border border-white/10 whitespace-nowrap">
        {product.category === 'whisky' ? t('card_cat_whisky', 'Whisky') : t('card_cat_gin', 'Gin')}
      </div>

      {/* Image */}
      <Link to={`/produto/${product.slug}`} className="block">
        <div className="relative h-72 bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] flex items-center justify-center overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            className="h-64 w-auto object-contain object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent opacity-60" />
        </div>
      </Link>

      {/* Info */}
      <div className="p-5">
        <p className="text-[#c9a84c]/70 text-[10px] tracking-[0.25em] uppercase mb-1 font-medium">
          {product.origin}{product.age ? ` · ${product.age}` : ''} · {product.abv}
        </p>
        <Link to={`/produto/${product.slug}`}>
          <h3
            className="text-white text-xl font-light mb-1 group-hover:text-[#c9a84c] transition-colors"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {product.name}
          </h3>
        </Link>
        <p className="text-white/40 text-xs mb-4 leading-relaxed line-clamp-2">
          {displayDescription}
        </p>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-[#c9a84c] text-xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {product.priceFormatted}
            </p>
          </div>
          <Link
            to={`/produto/${product.slug}`}
            className="bg-[#c9a84c] hover:bg-[#b8963e] text-black text-[10px] tracking-widest uppercase font-bold px-5 py-2.5 rounded-md transition-all duration-300 whitespace-nowrap"
          >
            {t('shop_buy', 'Comprar')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
